import type { Toast } from "@/types";

interface Props {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

const TYPE_STYLES: Record<
  Toast["type"],
  { border: string; icon: string; iconColor: string }
> = {
  info: {
    border: "border-l-stone-400",
    icon: "M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z",
    iconColor: "text-stone-400",
  },
  success: {
    border: "border-l-emerald-400",
    icon: "M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
    iconColor: "text-emerald-400",
  },
  warning: {
    border: "border-l-gold",
    icon: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z",
    iconColor: "text-gold",
  },
  error: {
    border: "border-l-brand-red",
    icon: "M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z",
    iconColor: "text-brand-red",
  },
};

export function ToastContainer({ toasts, onDismiss }: Props) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col-reverse gap-2 max-w-sm w-full pointer-events-none">
      {toasts.map((toast) => {
        const style = TYPE_STYLES[toast.type];
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto bg-white dark:bg-navy rounded-lg shadow-xl border border-stone-200 dark:border-navy-light border-l-[3px] ${style.border} animate-slide-up`}
          >
            <div className="flex items-start gap-2.5 px-3 py-2.5">
              {/* Icon */}
              <svg
                className={`w-4 h-4 flex-shrink-0 mt-0.5 ${style.iconColor}`}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d={style.icon}
                />
              </svg>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="font-display text-xs font-medium text-navy dark:text-white leading-tight">
                  {toast.title}
                </p>
                {toast.message && (
                  <p className="font-body text-xs text-stone-500 dark:text-stone-400 mt-0.5 leading-snug break-words">
                    {toast.message}
                  </p>
                )}
                {toast.action && (
                  <button
                    onClick={toast.action.onClick}
                    className="mt-1.5 font-body text-xs font-medium text-gold hover:text-gold-dark transition-colors"
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>

              {/* Dismiss */}
              <button
                onClick={() => onDismiss(toast.id)}
                className="flex-shrink-0 p-0.5 rounded text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
